<script setup lang="ts">
useSeoMeta({
  title: 'Products — QikSolve',
  description:
    'Explore QikSolve product pathways for regulated quality operations, including INQ eQMS and Qx governed record intelligence.',
});

const products = [
  {
    icon: 'i-heroicons-clipboard-document-check',
    title: 'INQ eQMS',
    family: 'INQ',
    description:
      'The core INQ quality management system pathway for regulated teams working within the Microsoft 365 environment they already use.',
    to: 'https://inq.qiksolve.com',
  },
  {
    icon: 'i-heroicons-academic-cap',
    title: 'INQ TMS',
    family: 'INQ',
    description: 'Support training management, competency, and evidence across regulated quality operations.',
    to: 'https://inq.qiksolve.com',
  },
  {
    icon: 'i-heroicons-document-text',
    title: 'INQ eDMS & eRMS',
    family: 'INQ',
    description:
      'Support controlled document and records management with traceable quality information for regulated teams.',
    to: 'https://inq.qiksolve.com',
  },
  {
    icon: 'i-heroicons-clipboard-document-check',
    title: 'QxReview',
    family: 'QxAIOS',
    description:
      'Agent-driven review of scanned paper records, including batch records, to surface potential ALCOA+ and consistency issues before human verification.',
    to: 'https://qx.qiksolve.com/QxReview',
  },
  {
    icon: 'i-heroicons-shield-check',
    title: 'QxVerify',
    family: 'QxAIOS',
    description:
      'Turn agent-reviewed paper-record data into a true digital copy for Quality review and sign-off, so approved records can be searched, traced, and reused for downstream quality insight.',
    to: 'https://qx.qiksolve.com/verify',
  },
  {
    icon: 'i-heroicons-chart-bar-square',
    title: 'QxInsight',
    family: 'QxAIOS',
    description: 'Turn verified digital-record data into usable information for quality and operational insight.',
    to: 'https://qx.qiksolve.com/insight',
  },
];
</script>

<template>
  <div class="bg-slate-50">
    <ArchetypeHero
      eyebrow="Products"
      title="Product pathways for quality work that needs to hold up."
      summary="QikSolve helps you find the product pathway that fits your quality and operational question. INQ supports controlled quality operations in Microsoft 365, while Qx helps teams turn paper records into governed, usable information."
      :cta="{ label: 'Start a conversation', to: '/contact' }"
    />

    <section class="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
      <div class="max-w-3xl">
        <p class="text-sm font-semibold uppercase tracking-wider text-brand-700">
          Product pathways
        </p>
        <h2 class="mt-2 text-3xl font-heading font-bold text-gray-900 sm:text-4xl">
          Choose the product pathway that fits your question.
        </h2>
        <p class="mt-5 text-lg leading-8 text-gray-700">
          Each product site provides the detailed workflows, capabilities, and next steps for its pathway. Start with
          the product family that matches the kind of quality work you need to control, review, or understand.
        </p>
      </div>

      <div class="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <a
          v-for="product in products"
          :key="product.title"
          :href="product.to"
          target="_blank"
          rel="noopener noreferrer"
          :class="[
            'group flex flex-col rounded-2xl border bg-white p-6 shadow-[var(--shadow-card)] transition-shadow hover:shadow-[var(--shadow-card-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600',
            product.family === 'INQ'
              ? 'border-violet-200 border-t-4 border-t-orange-400'
              : 'border-emerald-200 border-t-4 border-t-amber-400',
          ]"
        >
          <div
            :class="[
              'mb-5 flex size-11 items-center justify-center rounded-xl',
              product.family === 'INQ' ? 'bg-violet-50 text-violet-800' : 'bg-emerald-50 text-emerald-800',
            ]"
          >
            <UIcon
              :name="product.icon"
              class="size-6"
              aria-hidden="true"
            />
          </div>
          <span
            :class="[
              'w-fit rounded-full px-2.5 py-1 text-xs font-semibold',
              product.family === 'INQ'
                ? 'bg-violet-100 text-violet-900 ring-1 ring-orange-300'
                : 'bg-emerald-100 text-emerald-900 ring-1 ring-amber-300',
            ]"
          >
            {{ product.family }} product site
          </span>
          <h3 class="text-xl font-heading font-bold text-gray-900 group-hover:text-brand-700">
            {{ product.title }}
          </h3>
          <p class="mt-3 flex-1 text-gray-700">
            {{ product.description }}
          </p>
          <span class="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-700">
            Visit {{ product.family }} site
            <UIcon
              name="i-heroicons-arrow-up-right-20-solid"
              class="size-4"
              aria-hidden="true"
            />
          </span>
        </a>
      </div>

      <ArchetypeCta
        class="mt-12"
        title="Need help choosing a pathway?"
        content="Bring the quality or operational question you are working through. We can help identify a sensible next step."
        label="Contact QikSolve"
        to="/contact"
      />
    </section>
  </div>
</template>